import React, { useState, useEffect } from 'react';
import { Terminal, Settings, FileCode, Camera, RotateCw, Play, Square, Trash2, Globe, Code, ArrowLeft, Cpu, Activity, HardDrive, Info, Zap, Plus, Save, Key, Edit2, Lock, Shield, Link, Database, Copy, Check, Download, Loader2, Bot, Sparkles } from 'lucide-react';
import copyToClipboard from '../../utils/clipboard';
import '../../styles/dashboard.css';

const DeploymentDetailPage = ({
  appInfo,
  path,
  appName,
  ownerName,
  onBack,
  user,
  actionLoading,
  handleContainerAction,
  handleDeleteApp,
  handleRebuild,
  handleCreateSnapshot,
  handleDownloadSnapshot,
  openGlobalWorkspace,
  setTerminalApp,
  setShowTerminal,
  setEditingAppConfig,
  setEditingFileData,
  setFileBrowser,
  setShowFileEditor,
  loadFileBrowser,
  url,
  deploymentSnapshot,
  deploymentRuntimeLogs,
  deploymentRuntimeLoading,
  storageBytes
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showIdeDropdown, setShowIdeDropdown] = useState(false);
  const [startingIde, setStartingIde] = useState(false);
  const [copiedConn, setCopiedConn] = useState(false);
  
  // Settings Tab UI State
  const [envVars, setEnvVars] = useState([{ key: 'NODE_ENV', value: 'production' }]);
  const [customDomains, setCustomDomains] = useState(appInfo.domain && !appInfo.domain.includes('devorants.com') ? [{ domain: appInfo.domain, status: 'Active' }] : []);
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  
  // Dynamic Historical Metrics State
  const [cpuHistory, setCpuHistory] = useState([20, 25, 40, 30, 80, 55, 90, 45, 60, 35, 75, 50, 85, 40, 20, 30]);

  useEffect(() => {
    if (appInfo?.resources?.cpuPercent !== undefined) {
      setCpuHistory(prev => {
        // Only update if the value actually changed to prevent too many unnecessary re-renders, 
        // or just accept the new tick to make the graph "move" continuously
        return [...prev.slice(1), Math.min(100, appInfo.resources.cpuPercent)];
      });
    }
  }, [appInfo?.resources?.cpuPercent]);

  const isContainerized = appInfo.type === 'docker' || appInfo.type === 'compose' || appInfo.type === 'template' || appInfo.type === 'backend-workspace' || appInfo.type === 'ssr' || appInfo.metadata?.type === 'docker' || Boolean(appInfo.containerName) || Boolean(appInfo.metadata?.containerName);
  const isRunning = appInfo.status === 'running';
  const loadingKey = actionLoading[path] || actionLoading[appName];
  const isRepo = appInfo.source === 'repo' || appInfo.metadata?.source === 'repo';
  const isStatic = appInfo.type === 'static' || appInfo.status === 'static' || appInfo.metadata?.type === 'static';

  const formatVolumeBytes = (bytes) => {
    if (!bytes || bytes === 0) return null;
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAttachedVolumes = () => {
    let list = [];

    // 1. Live volumes from container inspect (highest fidelity)
    if (appInfo.resources?.volumes && Array.isArray(appInfo.resources.volumes) && appInfo.resources.volumes.length > 0) {
      list = appInfo.resources.volumes.map(v => ({
        name: v.name || (v.source ? v.source.split('/').pop() : 'volume'),
        destination: v.destination || '/',
        type: v.type || 'volume',
        size: formatVolumeBytes(v.size),
        readOnly: v.readOnly || false
      }));
    } else {
      // 2. Metadata volumes (if stopped or resources not yet polled)
      const rawVolumes = appInfo.metadata?.volumes || appInfo.volumes || [];
      if (Array.isArray(rawVolumes) && rawVolumes.length > 0) {
        list = rawVolumes.map(vol => {
          if (typeof vol === 'string') {
            const parts = vol.split(':');
            return {
              name: parts[0] || 'volume',
              destination: parts[1] || '/',
              type: parts[0].includes('/') ? 'bind' : 'volume',
              size: null,
              readOnly: parts[2] === 'ro'
            };
          }
          if (typeof vol === 'object' && vol !== null) {
            return {
              name: vol.name || (vol.mount ? vol.mount.split(':')[0] : 'volume'),
              destination: vol.path || (vol.mount ? vol.mount.split(':')[1] : '/'),
              type: vol.isNamedVolume ? 'volume' : 'bind',
              size: vol.sizeLimit || null,
              readOnly: false
            };
          }
          return null;
        }).filter(Boolean);
      } else if (appInfo.metadata?.volume && typeof appInfo.metadata.volume === 'object') {
        const v = appInfo.metadata.volume;
        list = [{
          name: v.name || `${appName}_data`,
          destination: v.path || `/var/lib/${appName}`,
          type: 'volume',
          size: null,
          readOnly: false
        }];
      }
    }

    // Filter out source code workspace mounts (e.g. host directory mounted to /home/app)
    return list.filter(v => v.destination !== '/home/app');
  };

  const attachedVolumes = getAttachedVolumes();

  // A deployment can expose several services (frontend, admin dashboard, user
  // dashboard, API). Keep the primary URL and all detected entry points visible.
  const serviceLabel = (entry, { primary = false } = {}) => {
    if (entry?.label) return entry.label;
    const pathHint = entry?.path || entry?.dir || entry?.role || '';
    const leaf = String(pathHint).split('/').filter(Boolean).pop() || '';
    const role = String(entry?.role || '').toLowerCase();
    if (role === 'admin' || /admin/i.test(leaf)) return 'Admin Dashboard';
    if (role === 'user' || /user/i.test(leaf)) return 'User Dashboard';
    if (role === 'backend' || role === 'api' || /backend|server|api/i.test(leaf)) return 'Backend API';
    if (role === 'frontend' || /front|client|web/i.test(leaf)) return 'Frontend';
    if (primary) return 'Primary Application';
    if (leaf) return leaf.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    if (role) return role.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return 'Service';
  };

  const deploymentDomains = (() => {
    const primaryPath = appInfo.metadata?.path || appInfo.metadata?.workDir || '.';
    const primaryRole = appInfo.role || appInfo.metadata?.role || 'app';
    const primary = {
      domain: appInfo.domain || url || (appInfo.port ? `${window.location.hostname}:${appInfo.port}` : ''),
      role: primaryRole,
      label: serviceLabel({ role: primaryRole, path: primaryPath }, { primary: true }),
      port: appInfo.metadata?.internalPort || appInfo.metadata?.port || appInfo.port,
      path: primaryPath
    };
    const fromEntryPoints = Array.isArray(appInfo.metadata?.entryPoints) ? appInfo.metadata.entryPoints : [];
    const fromBindings = Array.isArray(appInfo.metadata?.lastBindings) ? appInfo.metadata.lastBindings : [];
    const fromAdditional = Array.isArray(appInfo.additionalEntryPoints) ? appInfo.additionalEntryPoints : [];
    const extra = [...fromAdditional, ...fromEntryPoints, ...fromBindings].map((entry) => ({
      ...entry,
      path: entry.path || entry.dir || entry.directory || '',
      role: entry.role || entry.type || 'service',
      label: entry.label || serviceLabel(entry),
      domain: entry.domain || ''
    }));
    return [primary, ...extra].filter((entry, index, all) => entry.domain && all.findIndex(item => item.domain === entry.domain) === index);
  })();

  return (
    <div className="deployment-detail-page">
      <button className="ddp-back-btn" onClick={onBack}>
        <ArrowLeft size={18} /> Back to Dashboard
      </button>

      <div className="ddp-header-card">
        <div className="ddp-top-row">
          <div className="ddp-title-area">
            <div className="ddp-icon">
              {appInfo.type === 'static' ? <Globe size={32} /> : <Terminal size={32} />}
            </div>
            <div className="ddp-title-col">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0 }}>{appInfo.metadata?.displayName || appName}</h1>
                <span style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem', borderRadius: '6px', background: ownerName === user?.username ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(16, 185, 129, 0.12)', color: ownerName === user?.username ? 'var(--primary)' : '#10b981', border: `1px solid ${ownerName === user?.username ? 'rgba(var(--primary-rgb), 0.25)' : 'rgba(16, 185, 129, 0.25)'}`, fontWeight: 600 }}>
                  👤 Deployed by: @{ownerName}{ownerName === user?.username ? ' (You)' : ''}
                </span>
              </div>
              <a href={url || `http://${appInfo.domain}`} target="_blank" rel="noopener noreferrer" className="ddp-domain-link">
                {appInfo.domain || (appInfo.port ? `${window.location.hostname}:${appInfo.port}` : 'No Domain Configured')}
              </a>
            </div>
          </div>
          
          <div className={`dcc-status ${isRunning ? 'running' : (appInfo.status === 'static' ? 'static' : 'stopped')}`}>
            <span className="dcc-status-dot"></span>
            {isRunning ? 'Running' : (appInfo.status === 'static' ? 'Static' : appInfo.status)}
          </div>
        </div>

        <div className="ddp-tabs">
          <button className={`ddp-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
          <button className={`ddp-tab ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>Logs</button>
          <button className={`ddp-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
        </div>
      </div>

      <div className="ddp-content">
        {activeTab === 'overview' && (
          <div className="ddp-overview">
            <div className="ddp-overview-grid">
              
              {/* Left Column: Metrics & Info */}
              <div className="ddp-column-left">
                {!isStatic && (
                  <div className="ddp-panel" style={{ marginBottom: '1.5rem' }}>
                    <h3 className="ddp-panel-title"><Activity size={18} /> Live Resources</h3>
                    <div className="ddp-metrics-grid">
                      <div className="ddp-metric-box">
                        <div className="ddp-metric-label"><Cpu size={14} /> CPU</div>
                        <div className="ddp-metric-value">{appInfo.resources?.cpuPercent ? appInfo.resources.cpuPercent.toFixed(1) : '0.0'}%</div>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40px', opacity: 0.15 }}>
                          <svg viewBox="0 0 100 40" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                            <path
                              d={`M0,40 L0,${40 - (cpuHistory[0] || 0) * 0.4} ${cpuHistory.map((val, i) => `L${(i / (cpuHistory.length - 1)) * 100},${40 - val * 0.4}`).join(' ')} L100,40 Z`}
                              fill="#3b82f6"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="ddp-metric-box">
                        <div className="ddp-metric-label"><Zap size={14} /> RAM</div>
                        <div className="ddp-metric-value">{appInfo.resources?.memUsage ? (appInfo.resources.memUsage / 1024 / 1024).toFixed(2) : '0.00'} <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>MB</span></div>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40px', opacity: 0.15 }}>
                          <div style={{ width: `${Math.min(100, (appInfo.resources?.memUsage || 0) / (appInfo.resources?.memLimit || 1) * 100)}%`, height: '100%', background: 'linear-gradient(90deg, transparent, #a855f7)', transition: 'width 0.5s ease' }} />
                        </div>
                      </div>
                      <div className="ddp-metric-box">
                        <div className="ddp-metric-label"><HardDrive size={14} /> Storage</div>
                        <div className="ddp-metric-value">{storageBytes ? (storageBytes / 1024 / 1024).toFixed(2) : '0.00'} <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>MB</span></div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="ddp-panel">
                  <h3 className="ddp-panel-title"><Info size={18} /> Deployment Information</h3>
                  <div className="ddp-info-list">
                    <div className="ddp-info-item">
                      <div className="ddp-info-label">Application Type</div>
                      <div className="ddp-info-value" style={{ textTransform: 'capitalize' }}>{appInfo.type || 'Standard'}</div>
                    </div>
                    {isStatic && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Build / Site Size</div>
                        <div className="ddp-info-value">{formatVolumeBytes(storageBytes) || (storageBytes > 0 ? `${(storageBytes / 1024 / 1024).toFixed(2)} MB` : 'N/A (Static)')}</div>
                      </div>
                    )}
                    {!isStatic && (appInfo.metadata?.internalPort || appInfo.metadata?.port) && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Internal Port</div>
                        <div className="ddp-info-value">{appInfo.metadata?.internalPort || appInfo.metadata?.port}</div>
                      </div>
                    )}
                    {(appInfo.sshPort || appInfo.metadata?.sshPort) && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">SSH Port</div>
                        <div className="ddp-info-value">{appInfo.sshPort || appInfo.metadata?.sshPort}</div>
                      </div>
                    )}
                    {(appInfo.sshPassword || appInfo.metadata?.sshPassword) && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">SSH Password</div>
                        <div className="ddp-info-value" style={{ fontFamily: 'monospace' }}>{appInfo.sshPassword || appInfo.metadata?.sshPassword}</div>
                      </div>
                    )}
                    {appInfo.metadata?.extraPorts && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Extra Ports</div>
                        <div className="ddp-info-value">{Array.isArray(appInfo.metadata.extraPorts) ? appInfo.metadata.extraPorts.map(p => typeof p === 'object' ? `${p.host}→${p.container}` : p).join(', ') : appInfo.metadata.extraPorts}</div>
                      </div>
                    )}
                    {appInfo.source && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Source</div>
                        <div className="ddp-info-value" style={{ textTransform: 'capitalize' }}>{appInfo.source}</div>
                      </div>
                    )}
                    <div className="ddp-info-item">
                      <div className="ddp-info-label">Owner</div>
                      <div className="ddp-info-value">{ownerName}</div>
                    </div>
                    {appInfo.status && (
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Current Status</div>
                        <div className="ddp-info-value" style={{ color: isRunning ? 'var(--success)' : 'var(--text-secondary)', textTransform: 'capitalize' }}>{appInfo.status}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ddp-panel" style={{ marginTop: '1.5rem' }}>
                  <h3 className="ddp-panel-title"><Globe size={18} /> Deployment Domains</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0 0 1rem' }}>
                    Public URLs for this deployment, including dashboards and APIs.
                  </p>
                  <div className="ddp-domain-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                    {deploymentDomains.length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No domains configured.</div>
                    ) : deploymentDomains.map((entry, index) => {
                      const href = /^https?:\/\//i.test(entry.domain) ? entry.domain : `http://${entry.domain}`;
                      const label = serviceLabel(entry, { primary: index === 0 });
                      const pathDisplay = String(entry.path || entry.dir || '')
                        .replace(/^\/home\/app\//, './')
                        .replace(/^\.\/\//, './');
                      return (
                        <div key={`${entry.domain}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem 0.9rem', background: 'var(--surface-hover)', border: '1px solid var(--border-subtle)', borderRadius: '8px' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--text-main)' }}>
                              <Globe size={15} color="var(--primary)" />
                              <span>{label}{index === 0 ? ' · Primary' : ''}</span>
                            </div>
                            <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '0.25rem', color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {entry.domain}
                            </a>
                            <div style={{ marginTop: '0.2rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                              {pathDisplay && `Path: ${pathDisplay}`}{pathDisplay && entry.port ? ' · ' : ''}{entry.port && `Port: ${entry.port}`}
                            </div>
                          </div>
                          <a href={href} target="_blank" rel="noopener noreferrer" className="ddp-action-btn" style={{ flexShrink: 0, textDecoration: 'none' }}>Open</a>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Persistent Volumes Panel */}
                {(attachedVolumes.length > 0 || isContainerized) && (
                  <div className="ddp-panel" style={{ marginTop: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
                      <h3 className="ddp-panel-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <HardDrive size={18} /> Persistent Volumes {attachedVolumes.length > 0 && `(${attachedVolumes.length})`}
                      </h3>
                      {attachedVolumes.length > 0 && (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '0.2rem 0.55rem', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Active Storage
                        </span>
                      )}
                    </div>

                    {attachedVolumes.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                        {attachedVolumes.map((vol, idx) => {
                          const isSystemVolume = vol.name?.startsWith('deployment_') || vol.destination === '/app';
                          return (
                            <div
                              key={idx}
                              style={{
                                background: 'var(--surface-2)',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                padding: '0.75rem 0.9rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.45rem'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-main)' }}>
                                  <Database size={15} style={{ color: isSystemVolume ? 'var(--text-secondary)' : 'var(--primary)' }} />
                                  <span style={{ fontFamily: 'monospace' }}>{vol.name}</span>
                                  {isSystemVolume ? (
                                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--surface-hover)', padding: '0.12rem 0.45rem', borderRadius: '4px', border: '1px solid var(--border)' }}>
                                      System (Auto)
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', background: 'rgba(59, 130, 246, 0.1)', padding: '0.12rem 0.45rem', borderRadius: '4px', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
                                      User Storage
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {vol.size && (
                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--surface)', padding: '0.15rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }}>
                                      {vol.size}
                                    </span>
                                  )}
                                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: vol.readOnly ? '#f59e0b' : '#10b981', background: vol.readOnly ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)', padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
                                    {vol.readOnly ? 'RO' : 'RW'}
                                  </span>
                                </div>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                <span style={{ fontWeight: 500 }}>Mount Path:</span>
                                <code style={{ fontFamily: 'monospace', color: 'var(--text-main)', background: 'var(--surface)', padding: '0.1rem 0.45rem', borderRadius: '4px', border: '1px solid var(--border)' }}>
                                  {vol.destination}
                                </code>
                                {isSystemVolume ? (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    (Platform Workspace Storage)
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    (Custom Storage Mount)
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ margin: 0, fontSize: '0.84rem', color: 'var(--text-secondary)' }}>
                        No persistent volumes are attached to this container.
                      </p>
                    )}
                  </div>
                )}

                {(appInfo.metadata?.connectionString || appInfo.metadata?.templateId === 'custom-mysql' || (appInfo.image && appInfo.image.includes('mysql'))) && (
                  <div className="ddp-panel" style={{ marginTop: '1.25rem', border: '1px solid rgba(16, 185, 129, 0.25)', background: 'rgba(16, 185, 129, 0.03)' }}>
                    <h3 className="ddp-panel-title" style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Database size={18} /> Database Connection
                    </h3>
                    <div className="ddp-info-list" style={{ marginTop: '0.75rem' }}>
                      {appInfo.metadata?.dbUser && (
                        <div className="ddp-info-item">
                          <div className="ddp-info-label">Database User</div>
                          <div className="ddp-info-value" style={{ fontFamily: 'monospace' }}>{appInfo.metadata.dbUser}</div>
                        </div>
                      )}
                      {appInfo.metadata?.dbName && (
                        <div className="ddp-info-item">
                          <div className="ddp-info-label">Database Name</div>
                          <div className="ddp-info-value" style={{ fontFamily: 'monospace' }}>{appInfo.metadata.dbName}</div>
                        </div>
                      )}
                      <div className="ddp-info-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.4rem' }}>
                        <div className="ddp-info-label">Connection String</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', background: 'var(--surface-2)', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--border)' }}>
                          <code style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text-main)', wordBreak: 'break-all' }}>
                            {appInfo.metadata?.connectionString || `mysql://${appInfo.metadata?.dbUser || 'root'}:****@${appInfo.domain || window.location.hostname}:${appInfo.port || 3306}/${appInfo.metadata?.dbName || appName}`}
                          </code>
                          <button
                            type="button"
                            onClick={() => {
                              const str = appInfo.metadata?.connectionString || `mysql://${appInfo.metadata?.dbUser || 'root'}:****@${appInfo.domain || window.location.hostname}:${appInfo.port || 3306}/${appInfo.metadata?.dbName || appName}`;
                              copyToClipboard(str);
                              setCopiedConn(true);
                              setTimeout(() => setCopiedConn(false), 2000);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              padding: '0.35rem 0.65rem',
                              borderRadius: '6px',
                              border: '1px solid var(--border)',
                              background: copiedConn ? '#10b981' : 'var(--surface-3)',
                              color: copiedConn ? '#fff' : 'var(--text-main)',
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}
                            title="Copy connection string"
                          >
                            {copiedConn ? <Check size={13} /> : <Copy size={13} />}
                            <span>{copiedConn ? 'Copied' : 'Copy'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isRepo && (
                  <div className="ddp-panel">
                    <h3 className="ddp-panel-title"><RotateCw size={18} /> Git & CI/CD</h3>
                    <div className="ddp-info-list">
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Repository</div>
                        <div className="ddp-info-value" style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.85rem' }}>
                          {appInfo.metadata?.gitUrl || 'N/A'}
                        </div>
                      </div>
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Branch</div>
                        <div className="ddp-info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ display: 'inline-flex', padding: '0.15rem 0.5rem', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600 }}>
                            {appInfo.metadata?.branch || 'main'}
                          </span>
                        </div>
                      </div>
                      <div className="ddp-info-item">
                        <div className="ddp-info-label">Auto-Deploy Webhook</div>
                        <div className="ddp-info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Configured</span>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
              </div>

              {/* Right Column: Actions */}
              <div className="ddp-column-right">
                <div className="ddp-panel" style={{ marginBottom: '1.5rem' }}>
                  <h3 className="ddp-panel-title">Quick Actions</h3>
                  <div className="ddp-actions-grid">
                    
                    {url && (
                      <button className="ddp-action-btn primary" onClick={() => window.open(url, '_blank')}>
                        <Globe size={18} /> Open App
                      </button>
                    )}
                    
                    {!isStatic && (
                      <>
                        <button className="ddp-action-btn" onClick={() => {
                          if (openGlobalWorkspace) {
                            openGlobalWorkspace({
                              appName: appInfo.appName,
                              deploymentType: appInfo.type || 'template',
                              sshInfo: {
                                domain: appInfo.domain || `${appInfo.appName}.devorants.com`,
                                port: appInfo.port,
                                sshPort: appInfo.sshPort || appInfo.metadata?.sshPort,
                                extraPorts: appInfo.metadata?.extraPorts || null
                              },
                              autoDeploy: false,
                              initialTab: 'ai'
                            });
                          }
                        }}>
                          <Sparkles size={18} /> AI Workspace
                        </button>
                        <button className="ddp-action-btn" onClick={() => {
                          if (openGlobalWorkspace) {
                            openGlobalWorkspace({
                              appName: appInfo.appName,
                              deploymentType: appInfo.type || 'template',
                              sshInfo: {
                                domain: appInfo.domain || `${appInfo.appName}.devorants.com`,
                                port: appInfo.port,
                                sshPort: appInfo.sshPort || appInfo.metadata?.sshPort,
                                extraPorts: appInfo.metadata?.extraPorts || null
                              },
                              autoDeploy: false,
                              initialTab: 'files'
                            });
                          }
                        }}>
                          <Code size={18} /> Open Code
                        </button>
                        <div style={{ position: 'relative' }}>
                          <button className="ddp-action-btn" style={{ width: '100%' }} onClick={() => setShowIdeDropdown(!showIdeDropdown)}>
                            <Terminal size={18} /> Open IDE
                          </button>
                        {showIdeDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '0.5rem',
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            zIndex: 100,
                            overflow: 'hidden'
                          }}>
                            {[
                              { proto: 'vscode', label: 'VS Code', color: '#007acc', icon: <Code size={14} /> },
                              { proto: 'cursor', label: 'Cursor', color: 'var(--text-main)', icon: <Terminal size={14} /> },
                              { proto: 'antigravity', label: 'Antigravity', color: '#6366f1', icon: <Cpu size={14} /> }
                            ].map(ide => (
                              <button
                                key={ide.proto}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'var(--text-main)',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  borderBottom: ide.proto !== 'antigravity' ? '1px solid var(--border-subtle)' : 'none'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setStartingIde(ide.proto);
                                  try {
                                    await fetch(`/api/apps/${appInfo.name}/ide/start`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                    });
                                  } catch (err) {
                                    console.error("Failed to start IDE", err);
                                  }
                                  setStartingIde(false);
                                  const serverHost = window.location.hostname;
                                  const sshPort = appInfo.metadata?.sshPort || appInfo.sshPort || 22;
                                  const sshConfig = JSON.stringify({ hostName: serverHost, port: parseInt(sshPort), user: 'root' });
                                  const hexConfig = Array.from(new TextEncoder().encode(sshConfig)).map(b => b.toString(16).padStart(2, '0')).join('');
                                  window.location.href = `${ide.proto}://vscode-remote/ssh-remote+${hexConfig}/app`;
                                  setShowIdeDropdown(false);
                                }}
                              >
                                <span style={{ color: ide.color, display: 'flex', alignItems: 'center' }}>{ide.icon}</span>
                                {startingIde === ide.proto ? 'Starting...' : ide.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      </>
                    )}

                    {isContainerized && user && (user.role === 'admin' || ownerName === user.username) && (
                      <>
                        <button className="ddp-action-btn" onClick={() => {
                          setEditingFileData({ appName, owner: ownerName, filename: '', content: '', originalContent: '', loading: false, saving: false, error: null });
                          setFileBrowser({ entries: [], loading: false, currentDir: '', expandedDirs: {}, dirCache: {} });
                          setShowFileEditor(true);
                          loadFileBrowser(appName, ownerName, '');
                        }}>
                          <FileCode size={18} /> File Manager
                        </button>
                        
                        {isRunning && (
                          <button className="ddp-action-btn" onClick={() => {
                            setTerminalApp({ appName, owner: ownerName, mode: 'shell', command: '/bin/sh', sshConfig: null });
                            setShowTerminal(true);
                          }}>
                            <Terminal size={18} /> Terminal
                          </button>
                        )}

                        {appInfo.metadata?.type === "docker" && (
                          <>
                            <button className="ddp-action-btn" onClick={() => handleCreateSnapshot(path, appName, ownerName)} disabled={loadingKey === "snapshot"}>
                              {loadingKey === "snapshot" ? <Loader2 size={18} className="spin" /> : <Camera size={18} />} 
                              {loadingKey === "snapshot" ? ' Generating...' : ' Snapshot'}
                            </button>
                            {appInfo.metadata?.snapshots && appInfo.metadata.snapshots.length > 0 && (
                              <button className="ddp-action-btn" onClick={() => handleDownloadSnapshot(path, appName, ownerName)} disabled={loadingKey === "download_snapshot"}>
                                {loadingKey === "download_snapshot" ? <Loader2 size={18} className="spin" /> : <Download size={18} />} Download
                              </button>
                            )}
                          </>
                        )}
                        
                        {isRepo && (
                          <button className="ddp-action-btn" onClick={() => handleRebuild(path, appName, ownerName)} disabled={loadingKey === 'rebuild'}>
                            <RotateCw size={18} className={loadingKey === 'rebuild' ? 'spin' : ''} /> Rebuild
                          </button>
                        )}

                        {isRunning ? (
                          <button className="ddp-action-btn" onClick={() => handleContainerAction(path, appName, 'stop', ownerName)} disabled={loadingKey}>
                            <Square size={18} /> Stop App
                          </button>
                        ) : (
                          <button className="ddp-action-btn primary" onClick={() => handleContainerAction(path, appName, 'start', ownerName)} disabled={loadingKey}>
                            <Play size={18} /> Start App
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                <div className="ddp-panel danger-zone">
                  <h3 className="ddp-panel-title" style={{ color: '#ef4444' }}><Trash2 size={18} /> Danger Zone</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', marginTop: 0 }}>Permanently delete this deployment and all associated data. This action cannot be undone.</p>
                  {user && (user.role === 'admin' || ownerName === user.username) && (
                    <button className="ddp-action-btn danger" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { handleDeleteApp(path, appName); onBack(); }} disabled={loadingKey}>
                      <Trash2 size={18} /> Delete Deployment
                    </button>
                  )}
                </div>

                {!isStatic && (
                  <div className="ddp-panel" style={{ marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h3 className="ddp-panel-title" style={{ margin: 0 }}><Activity size={18} /> Historical CPU Usage</h3>
                      <select style={{ background: 'var(--surface-hover)', border: '1px solid var(--border)', color: 'var(--text-secondary)', padding: '0.4rem 0.8rem', borderRadius: '8px', fontSize: '0.85rem', outline: 'none' }}>
                        <option>Last 24 Hours</option>
                        <option>Last 7 Days</option>
                      </select>
                    </div>
                    
                    <div style={{ display: 'flex', position: 'relative', height: '160px', marginTop: '1rem', paddingBottom: '20px' }}>
                      {/* Y-Axis Labels */}
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: '1rem', color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 500, width: '45px', textAlign: 'right', paddingBottom: '20px' }}>
                        <span>100%</span>
                        <span>50%</span>
                        <span>0%</span>
                      </div>
                      
                      {/* Grid and Bars Container */}
                      <div style={{ flex: 1, position: 'relative', borderBottom: '1px solid var(--border-subtle)' }}>
                        {/* Dashed Grid Lines */}
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, borderTop: '1px dashed var(--border-subtle)' }}></div>
                        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed var(--border-subtle)' }}></div>
                        
                        {/* Bars */}
                        <div style={{ display: 'flex', gap: '4px', height: '100%', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
                          {cpuHistory.map((val, i) => (
                            <div 
                              key={i} 
                              title={`Time: T-${15 - i} | CPU: ${Math.round(val)}%`}
                              style={{ 
                                flex: 1, 
                                background: `linear-gradient(0deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.7) 100%)`, 
                                height: `${Math.max(2, val)}%`, 
                                borderRadius: '4px 4px 0 0', 
                                borderTop: '2px solid #3b82f6', 
                                transition: 'height 1s ease-in-out',
                                cursor: 'crosshair'
                              }}>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* X-Axis Labels */}
                      <div style={{ position: 'absolute', bottom: '0', left: '45px', right: 0, display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Yesterday</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, color: '#3b82f6' }}>Live Now</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="ddp-logs">
            {deploymentSnapshot?.status === 'failed' && (!deploymentRuntimeLogs[path] || (deploymentRuntimeLogs[path].logs || []).length === 0) && (
              <div style={{ color: '#ef4444', marginBottom: '1rem', padding: '1rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px' }}>
                <strong>Deployment Error:</strong> {deploymentSnapshot.error || 'The deployment failed.'}
              </div>
            )}
            <div className="ddp-terminal-box">
              {deploymentRuntimeLoading[path] && !isStatic ? (
                <div>Loading logs...</div>
              ) : (deploymentRuntimeLogs[path] && (deploymentRuntimeLogs[path].logs || []).length > 0) && !isStatic ? (
                deploymentRuntimeLogs[path].logs.map((line, index) => (
                  <div key={index} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: '0.25rem' }}>
                    {typeof line === 'object' && line !== null ? (line.message || JSON.stringify(line)) : line}
                  </div>
                ))
              ) : ((deploymentSnapshot?.logs && deploymentSnapshot.logs.length > 0) || (isStatic && Array.isArray(appInfo.metadata?.deploymentLogs) && appInfo.metadata.deploymentLogs.length > 0)) ? (
                <>
                  {isStatic && (
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#38bdf8', marginBottom: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #374151', paddingBottom: '0.35rem' }}>
                      📋 Deployment Snapshot Logs
                    </div>
                  )}
                  {((deploymentSnapshot?.logs && deploymentSnapshot.logs.length > 0) ? deploymentSnapshot.logs : (appInfo.metadata?.deploymentLogs || [])).map((line, index) => (
                    <div key={index} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: '0.25rem', color: '#e5e7eb' }}>
                      {typeof line === 'object' && line !== null ? (line.message || JSON.stringify(line)) : line}
                    </div>
                  ))}
                </>
              ) : isStatic ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem 0' }}>
                  <div style={{ color: '#38bdf8', fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    ⚡ Static Deployment
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.85rem', lineHeight: '1.5' }}>
                    This static website is served directly by the Nginx Web Server. No container runtime logs are generated.
                  </div>
                </div>
              ) : (
                <div style={{ color: '#9ca3af' }}>No logs available for this container.</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="ddp-overview-grid" style={isStatic ? { gridTemplateColumns: '1fr' } : {}}>
            {/* Left Column */}
            <div className="ddp-column-left" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div className="ddp-panel">
                <h3 className="ddp-panel-title"><Settings size={18} /> General Settings</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', marginTop: 0 }}>Basic configuration for your deployment.</p>
                <button className="ddp-action-btn primary" onClick={() => {
                  setEditingAppConfig({ path, appName, owner: ownerName, meta: { ...appInfo.metadata, internalPort: appInfo.metadata.internalPort || appInfo.metadata.port } });
                }}>
                  <Settings size={16} /> Open Full Configuration Editor
                </button>
              </div>

              <div className="ddp-panel">
                <h3 className="ddp-panel-title"><Link size={18} /> Custom Domains</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', marginTop: 0 }}>Attach custom domains and automatically provision SSL certificates.</p>
                
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: domainError ? '0.5rem' : '1.5rem' }}>
                  <input type="text" placeholder="e.g. api.myapp.com" value={newDomain} onChange={e => { setNewDomain(e.target.value); if (domainError) setDomainError(''); }} style={{ flex: 1, background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.65rem 1rem', color: 'var(--text-main)', fontSize: '0.9rem' }} />
                  <button className="ddp-action-btn" onClick={() => {
                    if (newDomain) {
                      if (/devorants\.com$/i.test(newDomain.trim())) {
                        setDomainError('devorants.com subdomains cannot be used as custom domains.');
                        return;
                      }
                      setDomainError('');
                      setCustomDomains([...customDomains, { domain: newDomain, status: 'Pending Verification' }]);
                      setNewDomain('');
                    }
                  }}>
                    <Plus size={16} /> Add
                  </button>
                </div>
                {domainError && <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1.25rem' }}>{domainError}</div>}

                <div className="ddp-env-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {customDomains.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.9rem', background: 'var(--surface-hover)', borderRadius: '8px' }}>No custom domains configured.</div>
                  ) : customDomains.map((d, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-hover)', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Globe size={16} color="var(--primary)" />
                        <span style={{ fontWeight: 500, fontSize: '0.95rem', color: 'var(--text-main)' }}>{d.domain}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem', borderRadius: '12px', background: d.status === 'Active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', color: d.status === 'Active' ? '#10b981' : '#f59e0b', fontWeight: 600 }}>{d.status}</span>
                        <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => setCustomDomains(customDomains.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            {!isStatic && (
              <div className="ddp-column-right">
                <div className="ddp-panel">
                  <h3 className="ddp-panel-title"><Key size={18} /> Environment Variables</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', marginTop: 0 }}>Manage the `.env` variables injected into this container at runtime.</p>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    <input type="text" placeholder="KEY (e.g. DB_PASS)" value={newEnvKey} onChange={e => setNewEnvKey(e.target.value)} style={{ width: '40%', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.65rem 1rem', color: 'var(--text-main)', fontSize: '0.9rem', fontFamily: 'monospace' }} />
                    <input type="text" placeholder="VALUE" value={newEnvValue} onChange={e => setNewEnvValue(e.target.value)} style={{ flex: 1, background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.65rem 1rem', color: 'var(--text-main)', fontSize: '0.9rem', fontFamily: 'monospace' }} />
                    <button className="ddp-action-btn" onClick={() => {
                      if (newEnvKey && newEnvValue) {
                        setEnvVars([...envVars, { key: newEnvKey, value: newEnvValue }]);
                        setNewEnvKey('');
                        setNewEnvValue('');
                      }
                    }}>
                      <Plus size={16} /> Add
                    </button>
                  </div>

                  <div className="ddp-env-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    {envVars.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.9rem', background: 'var(--surface-hover)', borderRadius: '8px' }}>No environment variables configured.</div>
                    ) : envVars.map((env, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input type="text" value={env.key} readOnly style={{ width: '40%', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none' }} />
                        <div style={{ position: 'relative', flex: 1 }}>
                          <input type="password" value={env.value} readOnly style={{ width: '100%', boxSizing: 'border-box', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none' }} />
                        </div>
                        <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setEnvVars(envVars.filter((_, idx) => idx !== i))}><Trash2 size={16} /></button>
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>
                    <button className="ddp-action-btn primary">
                      <Save size={16} /> Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeploymentDetailPage;
